/**
 * Module → Cognara suggestion registry.
 *
 * Each entry maps a moduleKey to:
 *   - persona: which agent persona to launch when this suggestion is clicked
 *   - prompts: 2-3 ready-to-use action prompts shown as a hover-able chip on
 *              the page header (via <CognaraSuggestionChip moduleKey="..."/>)
 *
 * These are tightly aligned with the PERSONA_GUIDE prompts in AgentChatPanel.jsx
 * but scoped to a specific module/page context (e.g. "Contracts page" gets
 * contract-specific prompts, not the generic Contract persona ones).
 */
export const MODULE_COGNARA = {
  // ── Procurement group ─────────────────────────────────────────
  taxonomy: {
    persona: 'category',
    prompts: [
      'Suggest taxonomy clean-up actions for our top-5 spend categories',
      'Find duplicate or near-duplicate category nodes',
    ],
  },
  'spend-cube': {
    persona: 'category',
    prompts: [
      'Top 10 maverick-spend categories last quarter and why',
      'Compare YoY spend by BU and flag anomalies above ±15%',
    ],
  },
  kraljic: {
    persona: 'category',
    prompts: [
      'Refresh the Kraljic quadrant and explain any quadrant movement',
      'Recommend a strategy for each category in the Bottleneck quadrant',
    ],
  },
  'market-intel': {
    persona: 'category',
    prompts: [
      'Summarise market signals for "Bulk chemicals" this quarter',
      'Compare benchmark prices vs our last 5 awards',
    ],
  },
  'category-strategy': {
    persona: 'category',
    prompts: [
      'Draft a 12-month strategy for "MRO consumables"',
      'Review the current strategy doc and flag stale assumptions',
    ],
  },
  demand: {
    persona: 'category',
    prompts: [
      'Forecast next quarter demand for our top-10 SKUs',
      'Surface demand-supply gaps and recommended sourcing actions',
    ],
  },
  // ── Supplier Management group ────────────────────────────────
  registration: {
    persona: 'onboarding',
    prompts: [
      'List pending registrations with stuck KYC and recommend next step',
      'Re-run financial health scoring on this week\'s incoming registrations',
    ],
  },
  qualification: {
    persona: 'onboarding',
    prompts: [
      'Show pending qualifications grouped by reason',
      'Auto-approve qualifications where all checks pass with score ≥ 85',
    ],
  },
  performance: {
    persona: 'performance',
    prompts: [
      'Which suppliers slipped below 85% OTIF in the last 90 days?',
      'Draft a balanced-scorecard refresh proposal for top-50 suppliers',
    ],
  },
  relationship: {
    persona: 'performance',
    prompts: [
      'Surface relationship hotspots — suppliers with declining QBR scores',
      'Recommend SRM actions for top-strategic suppliers this quarter',
    ],
  },
  'risk-mgmt': {
    persona: 'risk',
    prompts: [
      'Rank top-50 suppliers by composite risk this quarter',
      'Which active contracts have suppliers with Altman-Z < 1.8?',
    ],
  },
  diversity: {
    persona: 'diversity',
    prompts: [
      'YTD diverse-supplier spend by tier (MBE/WBE/Veteran/MSME)',
      'Which open sourcing events qualify for a diversity-first nudge?',
    ],
  },
  // ── Strategic Sourcing group ─────────────────────────────────
  'sourcing-strategy': {
    persona: 'sourcing',
    prompts: [
      'Which open sourcing events are at risk of missing bid-due deadlines?',
      'Suggest go-to-market strategy for category X with 3 supplier shortlist',
    ],
  },
  'supplier-invitation': {
    persona: 'sourcing',
    prompts: [
      'Build a long-list of 12 diverse suppliers for category steel-rebar',
      'Curate the long-list — auto-decline non-qualified suppliers',
    ],
  },
  clarifications: {
    persona: 'sourcing',
    prompts: [
      'Triage all open clarification questions and suggest routing',
      'Draft an addendum for the question with highest impact',
    ],
  },
  'bid-submission': {
    persona: 'sourcing',
    prompts: [
      'Show bid-receipt status and flag any deadline-breaching attempts',
      'Validate envelope integrity for event SEV-2026-014',
    ],
  },
  'bid-opening': {
    persona: 'sourcing',
    prompts: [
      'Run the bid-opening checklist for the latest event',
      'Generate comparison sheet (price + technical compliance)',
    ],
  },
  evaluation: {
    persona: 'sourcing',
    prompts: [
      'Score open bids on price + delivery + diversity criteria',
      'Highlight technical-evaluation outliers and explain why',
    ],
  },
  bafo: {
    persona: 'sourcing',
    prompts: [
      'Recommend BAFO targets based on benchmark + savings goal',
      'Draft a BAFO invitation message for shortlisted suppliers',
    ],
  },
  'award-recommendation': {
    persona: 'sourcing',
    prompts: [
      'Recommend award for SEV-2026-009 with DOA-routed approvers',
      'Compare top-2 bids on TCO and explain the gap',
    ],
  },
  'direct-award': {
    persona: 'sourcing',
    prompts: [
      'Draft a direct-award justification for an emergency procurement',
      'List sole-source suppliers requiring DOA pre-approval',
    ],
  },
  'communication-engine': {
    persona: 'sourcing',
    prompts: [
      'Show me failed email deliveries from the last 24 hours',
      'Which templates are missing a Spanish or French variant?',
    ],
  },
  'sourcing-analytics': {
    persona: 'sourcing',
    prompts: [
      'Where is our pipeline bottlenecked this quarter?',
      'Summarise validated savings vs target with the top-3 gap categories',
    ],
  },
  'supplier-portal-admin': {
    persona: 'onboarding',
    prompts: [
      'Audit recent supplier portal access patterns for anomalies',
      'Suggest a welcome message that emphasises diversity-first sourcing',
    ],
  },
  'itt-compliance': {
    persona: 'sourcing',
    prompts: [
      'Review the open ITT events for compliance gaps',
      'Has any below-threshold ITT in this category aggregated above the limit?',
    ],
  },
  framework: {
    persona: 'sourcing',
    prompts: [
      'Summarise active framework agreements and utilisation rate',
      'Suggest framework renewals coming due in the next 90 days',
    ],
  },
  // ── Contracts group ──────────────────────────────────────────
  'contract-types': {
    persona: 'contract',
    prompts: [
      'Audit our contract-type templates for missing standard clauses',
      'Suggest a new template structure for services-master agreements',
    ],
  },
  repository: {
    persona: 'contract',
    prompts: [
      'List active contracts > $5M with rising risk indicators',
      'Find contracts with auto-renewal triggering in the next 30 days',
    ],
  },
  'clause-library': {
    persona: 'contract',
    prompts: [
      'Find all "limitation of liability" clauses across services contracts',
      'Suggest stronger LD clauses based on industry benchmarks',
    ],
  },
  'obligation-tracking': {
    persona: 'contract',
    prompts: [
      'Which obligations are due in the next 30 days or already overdue?',
      'Flag contracts with chronic obligation slippage',
    ],
  },
  amendments: {
    persona: 'contract',
    prompts: [
      'Total cost impact of approved Change Orders on CTR-001 this fiscal',
      'Compare supplier-initiated vs buyer-initiated amendment trends',
    ],
  },
  // ── Claims & LD group (Iter-162 — full per-page coverage) ────
  'cld-register': {
    persona: 'risk',
    prompts: [
      'Show top-10 claims by probability-weighted exposure',
      'Which claims are within 14 days of their time-bar?',
      'Bulk-tag claims by claim type for the last fiscal quarter',
    ],
  },
  'cld-claim-initiation': {
    persona: 'risk',
    prompts: [
      'Pre-fill an FM claim from the latest declared event on CTR-001',
      'Draft a delay-claim notice for the late milestone in PRJ-007',
      'Validate the time-bar window before I submit this claim',
    ],
  },
  'cld-workbench': {
    persona: 'risk',
    prompts: [
      'Walk me through the 8 tabs of this workbench for claim CLM-042',
      'Which tab should I open next given the current claim state?',
      'Summarise this claim in one paragraph for the CFO',
    ],
  },
  'cld-portfolio-analytics': {
    persona: 'risk',
    prompts: [
      'Generate the boardroom PDF brief for the current quarter',
      'Show the dispute-risk heatmap by category and call out hot spots',
      'Compare YoY claim recovery rate and explain the delta',
    ],
  },
  'cld-predictive-risk': {
    persona: 'risk',
    prompts: [
      'Which 5 contracts are most likely to spawn a claim in the next 30 days?',
      'Explain the factor breakdown for the highest-risk contract',
      'Suggest mitigations for the top RED-band contract',
    ],
  },
  'cld-fm-notice': {
    persona: 'risk',
    prompts: [
      'Validate the FM declaration on CTR-007 using the latest weather data',
      'Draft the buyer response notice for the substantiated FM event',
      'Compare this FM event to historic precedents in our portfolio',
    ],
  },
  'cld-evidence': {
    persona: 'risk',
    prompts: [
      'Classify the latest 5 uploads and flag any gaps in chain-of-custody',
      'Find evidence linked to claim CLM-042 but mis-tagged in another claim',
      'Suggest the strongest evidence subset for the upcoming adjudication',
    ],
  },
  'cld-quantum': {
    persona: 'risk',
    prompts: [
      'Recommend the best-fit methodology for claim CLM-042 and why',
      'Run a Monte Carlo on the current cost-head breakdown',
      'Compare quantum P50 to the supplier\'s counter-offer',
    ],
  },
  'cld-ld-clause-library': {
    persona: 'risk',
    prompts: [
      'Find duplicate LD clauses and propose a canonical version',
      'Show usage stats: which LD clause is used the most this fiscal?',
      'Suggest deprecation candidates based on jurisdiction changes',
    ],
  },
  'cld-ld-config': {
    persona: 'risk',
    prompts: [
      'Apply the standard EPC LD clause to all contracts > ₹50 Cr that are missing one',
      'Audit LD rates that are above the §74 reasonable-pre-estimate threshold',
      'Show all contracts where LD is currently accruing',
    ],
  },
  'cld-ld-notice': {
    persona: 'risk',
    prompts: [
      'Dispatch LD notices for all newly capped accruals this week',
      'Bulk-acknowledge notices older than 14 days with supplier silence',
      'Draft the dispute-response letter for the contested LD notice',
    ],
  },
  'cld-negotiation': {
    persona: 'risk',
    prompts: [
      'Suggest a BATNA-anchored counter-offer for the current round',
      'Compute the gap-to-P50 and recommend a settlement zone',
      'Highlight any anchoring biases in the supplier\'s last 3 offers',
    ],
  },
  'cld-enforcement': {
    persona: 'risk',
    prompts: [
      'Model 3 settlement scenarios and recommend the best',
      'Generate the settlement certificate text for the approved scenario',
      'Forecast the recovery cash flow over the next 6 months',
    ],
  },
  'cld-adjudication': {
    persona: 'risk',
    prompts: [
      'Which claims should be referred to adjudication this quarter?',
      'Surface adjudication wins / losses by panel composition',
      'Estimate the recovery probability of an escalated claim',
    ],
  },
  'cld-adjudication-case': {
    persona: 'risk',
    prompts: [
      'Prepare the opening statement for the current case using the evidence pack',
      'Cross-reference the supplier\'s filings against our evidence repository',
      'Draft the rebuttal to the supplier\'s last filing',
    ],
  },
  'cld-recovery': {
    persona: 'risk',
    prompts: [
      'Show all aged-90+ recovery balances and recommend escalation',
      'Schedule deductions against the next 3 payable invoices',
      'Forecast end-of-quarter recovery percentage by supplier',
    ],
  },
  // Legacy keys kept for backward compatibility
  'event-detection': {
    persona: 'risk',
    prompts: [
      'Surface contracts with chronic delays — potential claim triggers',
      'Cross-check FM declarations against active force-majeure clauses',
    ],
  },
  'claim-initiation': {
    persona: 'risk',
    prompts: [
      'Pre-fill a FM claim using the latest declared event on CTR-001',
      'Draft a delay-claim notice for the late milestone in PRJ-007',
    ],
  },
  'claim-register': {
    persona: 'risk',
    prompts: [
      'Show top-10 claims by probability-weighted exposure',
      'Which claims are within 14 days of their time-bar?',
    ],
  },
  ld: {
    persona: 'risk',
    prompts: [
      'Calculate LD recovery for all delayed contracts this fiscal',
      'Draft LD waiver justification for the highest-value supplier dispute',
    ],
  },
};

/** Get the suggestion entry for a moduleKey (or null if not registered). */
export const getModuleCognara = (moduleKey) =>
  (moduleKey && MODULE_COGNARA[moduleKey]) || null;
